import React from 'react';
import { createPortal } from 'react-dom';
import {
  Calendar,
  Clock,
  Share2,
  Bookmark,
  ChevronLeft,
  BookOpen,
} from 'lucide-react';
import { generateBlogSchemaMarkup } from './blogPostUtils.js';
import { BreadcrumbNav } from '../../../../shared/components/BreadcrumbNav.js';

interface BlogHeroHeaderProps {
  post: any;
  readingTime: number;
  scrollProgress: number;
  onBack?: () => void;
  onShare: () => void;
  isBookmarked: boolean;
  onToggleBookmark: () => void;
}

export const BlogHeroHeader: React.FC<BlogHeroHeaderProps> = ({
  post,
  readingTime,
  scrollProgress,
  onBack,
  onShare,
  isBookmarked,
  onToggleBookmark,
}) => {
  const schemaMarkup = generateBlogSchemaMarkup(post);

  return (
    <>
      {/* Dynamic Schema.org JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(schemaMarkup)}
      </script>

      {/* Dynamic Reader Scroll Progress Bar (Teleported to document.body to escape transform/clipping contexts) */}
      {typeof document !== 'undefined' && createPortal(
        <div className="fixed top-0 left-0 right-0 h-[5px] bg-slate-100/20 z-[9999] pointer-events-none">
          <div
            className="h-full bg-linear-to-r from-indigo-600 via-indigo-500 to-sky-400 transition-all duration-75 shadow-[0_2px_12px_rgba(79,70,229,0.75)]"
            style={{ width: `${scrollProgress}%` }}
          />
        </div>,
        document.body
      )}

      {/* Top Sticky Navigation */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-100 px-6 sm:px-12 lg:px-16 py-3">
        <div className="w-full flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <a
              href="/blog"
              onClick={(e) => {
                e.preventDefault();
                if (onBack) {
                  onBack();
                }
              }}
              className="flex items-center gap-1.5 text-slate-600 hover:text-indigo-600 font-bold transition-colors text-sm cursor-pointer shrink-0"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Kembali</span>
            </a>
            <div className="hidden md:block h-4 w-px bg-slate-200" />
            <BreadcrumbNav
              items={[
                { name: 'Beranda', url: '/' },
                { name: 'Blog', url: '/blog' },
                ...(post.category ? [{ name: post.category, url: `/blog?cat=${encodeURIComponent(post.category)}` }] : []),
                { name: post.title, url: `/blog/${post.slug}` },
              ]}
              className="hidden sm:flex"
            />
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onShare}
              className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors cursor-pointer"
              title="Bagikan Artikel"
            >
              <Share2 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={onToggleBookmark}
              className={`p-2 rounded-xl transition-all cursor-pointer ${
                isBookmarked 
                  ? 'bg-amber-50 text-amber-600 border border-amber-200/50 hover:bg-amber-100' 
                  : 'hover:bg-slate-100 text-slate-500'
              }`}
              title={isBookmarked ? 'Hapus Bookmark' : 'Simpan ke Bookmark'}
            >
              <Bookmark className={`w-4 h-4 ${isBookmarked ? 'fill-current' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <header className="w-full px-6 sm:px-12 lg:px-16 pt-16 pb-12">
        <div className="space-y-8 text-center max-w-none">
          <div className="flex items-center justify-center gap-2 text-[10px] font-bold text-indigo-600 uppercase tracking-[0.2em]">
            <span className="w-8 h-px bg-indigo-200" />
            {post.category || 'Edukasi Penulisan & Literasi'}
            <span className="w-8 h-px bg-indigo-200" />
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.1] max-w-none">
            {post.title}
          </h1>

          {post.subtitle && (
            <p className="text-xl sm:text-2xl font-medium text-slate-500 leading-relaxed mx-auto italic font-serif max-w-none">
              "{post.subtitle}"
            </p>
          )}

          {post.tags && (
            <div className="flex flex-wrap items-center justify-center gap-2 pt-4">
              {post.tags.split(',').map((tag: string, tIdx: number) => (
                <span key={`header-tag-${tIdx}`} className="inline-flex items-center px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-100/80 text-[11px] font-bold text-indigo-700 uppercase tracking-wider">
                  #{tag.trim()}
                </span>
              ))}
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 pt-8 border-t border-slate-100 mx-auto mt-12 w-full">
            <div className="flex items-center gap-4 text-left">
              <div className="w-14 h-14 rounded-2xl bg-linear-to-tr from-indigo-600 to-sky-500 text-white font-bold flex items-center justify-center shadow-xl shadow-indigo-500/20 text-xl border-2 border-white ring-4 ring-indigo-50/50">
                {post.authorName.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-base font-bold text-slate-900 leading-tight">{post.authorName}</p>
                <p className="text-xs text-slate-500 font-medium tracking-wide uppercase mt-1">
                  {post.authorRole || 'Tim Redaksi UIBN'}
                </p>
              </div>
            </div>

            <div className="hidden sm:block w-px h-10 bg-slate-200" />

            <div className="flex flex-col items-center sm:items-start gap-1 text-slate-500">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400">
                <Calendar className="w-3.5 h-3.5" />
                <span>Tanggal Terbit</span>
              </div>
              <span className="text-sm font-bold text-slate-700">
                {new Date(post.publishedAt || post.createdAt).toLocaleDateString('id-ID', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
            </div>

            <div className="flex flex-col items-center sm:items-start gap-1 text-slate-500">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400">
                <Clock className="w-3.5 h-3.5" />
                <span>Estimasi</span>
              </div>
              <span className="text-sm font-bold text-slate-700">
                {readingTime} Menit Baca
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Featured Banner (Minimalist & Abstract) */}
      <div className="max-w-5xl mx-auto px-4 mb-20">
        <div className="aspect-[21/9] rounded-[3rem] bg-linear-to-br from-indigo-50 to-white flex items-center justify-center p-8 relative overflow-hidden border border-indigo-100/50 shadow-2xl shadow-indigo-100">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_50%_50%,#6366f1,transparent)] animate-pulse" />
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 blur-[100px] rounded-full" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-sky-500/5 blur-[100px] rounded-full" />

          <div className="relative z-10 text-center">
            <BookOpen className="w-20 h-20 text-indigo-200 mx-auto mb-6" />
            <div className="text-indigo-400 text-xs font-black uppercase tracking-[0.3em] opacity-40">
              Ulasan Literasi Kepenulisan & Penerbitan
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
